import { useState } from "react";

export function InterfaceColumn() {
  const [hoveredBrand, setHoveredBrand] = useState<string | null>(null);

  return (
    <div className="flex flex-col">
      <p className="font-sans text-[10px] font-medium uppercase tracking-[0.3em] text-[hsl(0,0%,40%)]">
        Interface
      </p>

      {/* Side-by-side comparison */}
      <div className="mt-8 grid grid-cols-2 gap-4">
        {/* Basalte */}
        <div
          className="group cursor-pointer"
          onMouseEnter={() => setHoveredBrand("basalte")}
          onMouseLeave={() => setHoveredBrand(null)}
        >
          <div className="relative overflow-hidden rounded-lg border border-[hsl(0,0%,12%)] bg-[hsl(0,0%,5%)] transition-all duration-500 hover:border-[hsl(38,25%,25%)]">
            {/* Brushed brass surface simulation */}
            <div className="relative h-44 overflow-hidden">
              <div
                className="absolute inset-0 transition-all duration-700"
                style={{
                  background: `linear-gradient(135deg, 
                    hsl(38, 30%, 12%) 0%, 
                    hsl(38, 25%, 18%) 25%, 
                    hsl(38, 20%, 14%) 50%, 
                    hsl(38, 28%, 20%) 75%, 
                    hsl(38, 22%, 13%) 100%)`,
                  opacity: hoveredBrand === "basalte" ? 1 : 0.7,
                }}
              />
              {/* Grain texture overlay */}
              <div
                className="absolute inset-0 opacity-30 mix-blend-overlay"
                style={{
                  backgroundImage: `repeating-linear-gradient(
                    90deg,
                    transparent,
                    transparent 1px,
                    hsl(38, 15%, 25%) 1px,
                    hsl(38, 15%, 25%) 2px
                  )`,
                  backgroundSize: "3px 3px",
                }}
              />
              {/* Highlight sweep on hover */}
              <div
                className="absolute inset-0 transition-all duration-1000"
                style={{
                  background: `linear-gradient(120deg, transparent 30%, hsl(38, 30%, 30%) 50%, transparent 70%)`,
                  opacity: hoveredBrand === "basalte" ? 0.3 : 0,
                  transform: hoveredBrand === "basalte" ? "translateX(0)" : "translateX(-100%)",
                }}
              />
              {/* Center touch indicator */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="h-1 w-1 rounded-full transition-all duration-500"
                  style={{
                    backgroundColor: "hsl(38, 30%, 55%)",
                    boxShadow: hoveredBrand === "basalte"
                      ? "0 0 20px hsl(38, 30%, 40%), 0 0 40px hsl(38, 20%, 25%)"
                      : "0 0 6px hsl(38, 20%, 30%)",
                    transform: hoveredBrand === "basalte" ? "scale(1.8)" : "scale(1)",
                  }}
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <h3 className="font-sans text-sm font-light text-[hsl(0,0%,88%)]">Basalte</h3>
            <p className="mt-0.5 font-sans text-[11px] text-[hsl(0,0%,45%)]">Material-Driven Interface</p>
          </div>
        </div>

        {/* Gira */}
        <div
          className="group cursor-pointer"
          onMouseEnter={() => setHoveredBrand("gira")}
          onMouseLeave={() => setHoveredBrand(null)}
        >
          <div className="relative overflow-hidden rounded-lg border border-[hsl(0,0%,12%)] bg-[hsl(0,0%,5%)] transition-all duration-500 hover:border-[hsl(0,0%,22%)]">
            {/* Matte black glass surface */}
            <div className="relative h-44 overflow-hidden">
              <div
                className="absolute inset-0 transition-all duration-700"
                style={{
                  background: `linear-gradient(160deg,
                    hsl(0, 0%, 6%) 0%,
                    hsl(0, 0%, 9%) 40%,
                    hsl(0, 0%, 7%) 60%,
                    hsl(0, 0%, 10%) 100%)`,
                  opacity: hoveredBrand === "gira" ? 1 : 0.8,
                }}
              />
              {/* Glass reflection */}
              <div
                className="absolute inset-0 transition-all duration-1000"
                style={{
                  background: `linear-gradient(135deg, transparent 40%, hsl(0, 0%, 18%) 50%, transparent 60%)`,
                  opacity: hoveredBrand === "gira" ? 0.4 : 0.1,
                  transform: hoveredBrand === "gira" ? "translateX(0)" : "translateX(-80%)",
                }}
              />
              {/* Minimal keypad lines */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex flex-col gap-4">
                  {[0, 1].map((row) => (
                    <div key={row} className="flex gap-4">
                      {[0, 1].map((col) => (
                        <div
                          key={col}
                          className="h-6 w-6 rounded-[2px] border transition-all duration-500"
                          style={{
                            borderColor: hoveredBrand === "gira"
                              ? "hsl(0, 0%, 28%)"
                              : "hsl(0, 0%, 16%)",
                            backgroundColor: hoveredBrand === "gira"
                              ? "hsl(0, 0%, 10%)"
                              : "hsl(0, 0%, 7%)",
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <h3 className="font-sans text-sm font-light text-[hsl(0,0%,88%)]">Gira</h3>
            <p className="mt-0.5 font-sans text-[11px] text-[hsl(0,0%,45%)]">Architectural Minimalism</p>
          </div>
        </div>
      </div>

      {/* Keywords */}
      <div className="mt-8 flex gap-6">
        {["Material", "Touch", "Detail"].map((kw) => (
          <span
            key={kw}
            className="font-sans text-[9px] uppercase tracking-[0.3em] text-[hsl(0,0%,30%)]"
          >
            {kw}
          </span>
        ))}
      </div>
    </div>
  );
}
